
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';
import renderHTML from 'react-render-html';
import ReactGA from 'react-ga';
import { I18n } from 'react-redux-i18n';

import { Container, Image, Grid } from 'semantic-ui-react';
import ColoredButtonLink from '../../components/common/coloredButtonLink';
import Video from '../../components/common/video';
import Footer from '../../components/common/footer';
import Layouts from '../../components/layouts/index';
import ShareButton from '../../components/common/shareButton';

import { replaceTextUsingTemplate } from '../shared/shareTemplate';
import { mediaTypes } from '../../constants/mediaTypes';
import { donationPaymentType } from '../../constants/donationTypeOptions';
import { TrackerNames } from '../../constants/webTracking';
import { isArrayWithItems } from '../../helpers/arrayHelper';
import CurrencyHelper from "../../helpers/currencyHelper";

class ThankyouPage extends React.Component {
    componentDidMount() {
        window.scrollTo(0, 0)

        ReactGA.pageview(window.location.pathname, [TrackerNames.clientTrackerName]);
    }

    getDonatedPageUrl = () => {
        return this.props.match.url.replace('thankyou', '');
    }

    getReturnUrl = () => {
        return this.props.returnUrl || this.getDonatedPageUrl();
    }

    getPageShareUrl = () => {
        return typeof window !== 'undefined'
            ? window.location.href
            : '';
    }

    getMediaComponent = () => {
        const { selectedMediaType, thankYouImageUrl, thankYouVideoUrl } = this.props;

        if (selectedMediaType === mediaTypes.IMAGE && thankYouImageUrl) {
            return <Image src={this.props.thankYouImageUrl} />
        }

        if (selectedMediaType === mediaTypes.VIDEO && thankYouVideoUrl) {
            return <Video value={thankYouVideoUrl} />
        }

        return null;
    }

    getThankYouMessageWithReplacedText = () => {
        return replaceTextUsingTemplate({
            text: this.props.thankYouMessage,
            organisationName: this.props.organisationName,
            email: this.props.donorEmail,
            firstName: this.props.donorFirstName,
            receiptNumber: this.props.receiptNumber,
            donationId: this.props.donationId
        }) || `Thanks ${this.props.donorFirstName}`;
    }

    getThankyouHeaderText = () => {
        const customMessage = this.props.paymentType !== donationPaymentType.pledge.value ?
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.default.header") :
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.pledge.header");

        if (customMessage) {
            return customMessage
                .replace(/{organisationName}/g, this.props.organisationName)
                .replace(/{email}/g, this.props.donorEmail)
                .replace(/{firstName}/g, this.props.donorFirstName)
                .replace(/{receiptNumber}/g, this.props.receiptNumber)
                .replace(/{donationId}/g, this.props.donationId);
        }

        const defaultText = this.getThankYouMessageWithReplacedText();
        return defaultText;
    };

    getThankyouParagraphText = () => {
        const customMessage = this.props.paymentType !== donationPaymentType.pledge.value ?
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.default.paragraph") :
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.pledge.paragraph");

        if (customMessage) {
            return customMessage
                .replace(/{organisationName}/g, this.props.organisationName)
                .replace(/{email}/g, this.props.donorEmail)
                .replace(/{firstName}/g, this.props.donorFirstName)
                .replace(/{receiptNumber}/g, this.props.receiptNumber)
                .replace(/{donationId}/g, this.props.donationId);
        }

        return `We have emailed your receipt to <b>${this.props.donorEmail}</b>`;
    };

    getThankyouMessageText = () => {
        const customMessage = this.props.paymentType !== donationPaymentType.pledge.value ?
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.default.text") :
            get(this.props.campaign, "template.value.customText.standaloneDonationForm.thankyouPage.pledge.text");

        if (customMessage) {
            return customMessage
                .replace(/{organisationName}/g, this.props.organisationName)
                .replace(/{email}/g, this.props.donorEmail)
                .replace(/{firstName}/g, this.props.donorFirstName)
                .replace(/{receiptNumber}/g, this.props.receiptNumber)
                .replace(/{donationId}/g, this.props.donationId);
        }

        return "Please continue your great work by sharing this cause with everyone you know. Simply click on the sharing buttons below to share by email, Facebook, Twitter or Linkedin";
    };

    getGiftMatchingText = () => {
        const giftMatching = get(this.props.createdPaymentRecord, 'giftMatching.entries');
        if (isArrayWithItems(giftMatching)) {
            return (
                <React.Fragment>
                    {
                        React.Children.toArray(
                            giftMatching.map(item => {
                                return (
                                    <div>
                                        {item.mainImageUrl && <div className="gift-matching-logo">
                                            <img src={item.mainImageUrl} alt=""/>
                                        </div>}
                                        {
                                            renderHTML(I18n.t('thankyou.giftMatching', {
                                                amount: CurrencyHelper.formatCurrencyNumber(item.amount),
                                                entity: item.entity ? `by ${item.entity}` : ""
                                            }))
                                        }
                                    </div>
                                )
                            })
                        )
                    }
                    <br />
                </React.Fragment>
            );
        }

        return <React.Fragment />;
    }

    render() {
        const shareUrl = this.getPageShareUrl().replace('thankyou', '');
        const mediaComponent = this.getMediaComponent();
        const thankYouHeader = this.getThankyouHeaderText();
        const thankYouParagraph = this.getThankyouParagraphText();
        const thankYouMessage = this.getThankyouMessageText();
        const donatePageUrl = this.getReturnUrl();
        const pixelUrl = get(this.props, 'campaign.template.value.customSetup.standAloneClient.donationSetup.thankYouPagePixelUrl');        
        const giftMatchingText = this.getGiftMatchingText();
        const hideShareWidget = get(this.props.campaign, 'template.value.customSetup.standaloneDonationForm.hideShareWidget');

        return (
            <Layouts.Wizard>
                <div className="donate-wizard thank-you-page">
                    <Container className="steps-cont" textAlign="center">
                        {mediaComponent}
                        <h2>{renderHTML(thankYouHeader)}</h2>
                        <span>{renderHTML(thankYouParagraph)}</span>
                        <p>{renderHTML(thankYouMessage)}</p>
                        {giftMatchingText}
                        <div className="thank-you-buttons">
                            <Grid>
                                {
                                    !hideShareWidget &&
                                    <Grid.Row>
                                        <ShareButton shareUrl={shareUrl}>Share</ShareButton>
                                    </Grid.Row>
                                }
                                <Grid.Row>
                                    <Grid.Column>
                                        <ColoredButtonLink url={donatePageUrl}
                                            text="Return to page"
                                            fluid={false} />
                                    </Grid.Column>
                                </Grid.Row>

                            </Grid>
                        </div>
                    </Container>
                    {pixelUrl && <img alt="" data-testid="pixelUrl" src={pixelUrl} width="1" height="1" />}
                </div>
                <Footer />
            </Layouts.Wizard>
        )
    }
}

const mapState = (state) => {
    return {
        organisationName: get(state, 'page.organization.name'),

        campaign: get(state, 'page.campaign'),
        campaignName: get(state, "page.campaign.name"),
        campaignURL: get(state, "page.campaign.urlFull"),

        thankYouMessage: get(state, "page.campaign.thankYouMessage"),
        thankYouImageUrl: get(state, "page.campaign.thankYouImageUrl"),
        thankYouVideoUrl: get(state, "page.campaign.thankYouVideoUrl"),
        selectedMediaType: get(state, "page.campaign.selectedMediaType"),
        returnUrl: get(state, "page.campaign.template.value.donationSetup.returnUrl"),

        donorFirstName: get(state, "donation.customer.firstName"),
        donorEmail: get(state, "donation.customer.email"),

        pageType: get(state, 'page.pageType'),
        pageName: get(state, 'page.campaign.name'),
        targetAmount: get(state, 'page.campaign.targetAmount'),
        pageUrl: get(state, 'page.campaign.urlFull'),
        paymentType: get(state, 'donation.paymentType'),

        donationId: get(state, 'donation.createdPaymentRecord.id'),
        receiptNumber: get(state, 'donation.createdPaymentRecord.receiptNumber'),
        createdPaymentRecord: get(state, 'donation.createdPaymentRecord')
    };
};

export default withRouter(connect(mapState)(ThankyouPage));
